import {
  deleteObject,
  getDownloadURL,
  ref,
  uploadBytes,
} from 'firebase/storage';
import {
  doc,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { auth, db, storage } from '../firebase';

export const AVATAR_MAX_FILE_SIZE = 5 * 1024 * 1024;
export const AVATAR_ALLOWED_TYPES = Object.freeze([
  'image/jpeg',
  'image/png',
  'image/webp',
]);

export const validateAvatarFile = (file) => {
  if (!file || !AVATAR_ALLOWED_TYPES.includes(file.type)) {
    return 'Підтримуються лише JPG, PNG або WebP';
  }

  if (file.size > AVATAR_MAX_FILE_SIZE) {
    return 'Максимальний розмір фото — 5 МБ';
  }

  return '';
};

const loadImage = (file) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file);
  const image = new Image();

  image.onload = () => {
    URL.revokeObjectURL(url);
    resolve(image);
  };
  image.onerror = () => {
    URL.revokeObjectURL(url);
    reject(new Error('invalid-image'));
  };
  image.src = url;
});

export const compressAvatar = async (file, targetSize = 512) => {
  const image = await loadImage(file);
  const cropSize = Math.min(image.naturalWidth, image.naturalHeight);
  const sourceX = (image.naturalWidth - cropSize) / 2;
  const sourceY = (image.naturalHeight - cropSize) / 2;
  const canvas = document.createElement('canvas');
  canvas.width = targetSize;
  canvas.height = targetSize;

  const context = canvas.getContext('2d');
  context.drawImage(
    image,
    sourceX,
    sourceY,
    cropSize,
    cropSize,
    0,
    0,
    targetSize,
    targetSize
  );

  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (result) => result ? resolve(result) : reject(new Error('compression-failed')),
      'image/webp',
      0.84
    );
  });

  return blob;
};

const requireCurrentUser = async () => {
  const currentUser = auth.currentUser;

  if (!currentUser?.uid) {
    const error = new Error('Сесія завершилась. Увійдіть повторно');
    error.code = 'storage/unauthenticated';
    throw error;
  }

  await currentUser.getIdToken();
  return currentUser;
};

export const uploadUserAvatar = async (file, previousAvatarPath = null) => {
  const validationError = validateAvatarFile(file);
  if (validationError) throw new Error(validationError);

  const currentUser = await requireCurrentUser();
  const uid = currentUser.uid;
  const compressedAvatar = await compressAvatar(file);
  const avatarPath = `users/${uid}/avatar/avatar-${Date.now()}.webp`;
  const avatarRef = ref(storage, avatarPath);
  const metadata = {
    contentType: compressedAvatar.type || 'image/webp',
    cacheControl: 'public,max-age=3600',
  };

  if (process.env.NODE_ENV === 'development') {
    console.info('[AVATAR UPLOAD]', {
      uid,
      storagePath: avatarPath,
      originalSize: file.size,
      blobSize: compressedAvatar.size,
      blobType: compressedAvatar.type,
      metadataContentType: metadata.contentType,
    });
  }

  await uploadBytes(avatarRef, compressedAvatar, metadata);
  const photoURL = await getDownloadURL(avatarRef);

  try {
    await updateDoc(doc(db, 'users', uid), {
      photoURL,
      avatarPath,
      photoUpdatedAt: serverTimestamp(),
    });
  } catch (error) {
    await deleteObject(avatarRef).catch(() => {});
    throw error;
  }

  if (
    previousAvatarPath &&
    previousAvatarPath !== avatarPath &&
    previousAvatarPath.startsWith(`users/${uid}/avatar/`)
  ) {
    try {
      await deleteObject(ref(storage, previousAvatarPath));
    } catch (error) {
      if (
        process.env.NODE_ENV === 'development' &&
        error?.code !== 'storage/object-not-found'
      ) {
        console.warn('Не вдалося видалити попередній аватар:', error);
      }
    }
  }

  return { photoURL, avatarPath };
};

export const removeUserAvatar = async (avatarPath) => {
  const currentUser = await requireCurrentUser();
  const uid = currentUser.uid;
  const isOwnedAvatar = avatarPath?.startsWith(`users/${uid}/avatar/`);

  if (isOwnedAvatar) {
    try {
      await deleteObject(ref(storage, avatarPath));
    } catch (error) {
      if (error?.code !== 'storage/object-not-found') throw error;
    }
  }

  await updateDoc(doc(db, 'users', uid), {
    photoURL: null,
    avatarPath: null,
    photoUpdatedAt: serverTimestamp(),
  });
};
